import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Copy, Share2, Users, Award, TrendingUp, ArrowLeft, Gift, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ENLogo } from '@/components/ENLogo';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicUrl } from '@/lib/publicUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteFriends() {
  const { user, getAuthHeaders } = useAuth();
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const response = await fetch(`${API}/supporters/my-referral-info`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) setReferralInfo(await response.json());
    } catch (error) {
      console.error('Failed to fetch referral info:', error);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = publicUrl(`/browse?ref=${referralInfo?.promo_code}`);

  const copyCode = () => {
    if (referralInfo?.promo_code) {
      navigator.clipboard.writeText(referralInfo.promo_code);
      setCopied(true);
      toast.success('Promo code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopiedLink(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join EntrepreNatives',
          text: `Check out EntrepreNatives — a global Indigenous vendor marketplace! Use my code ${referralInfo?.promo_code} to get started.`,
          url: referralLink,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Check out EntrepreNatives — a global Indigenous vendor marketplace! Use my code ${referralInfo?.promo_code} to get started: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(`Join @EntrepreNatives — a global Indigenous vendor marketplace! Use my referral code: ${referralInfo?.promo_code}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const stats = [
    { label: 'Referrals', value: referralInfo?.referral_count || 0, icon: Users, color: 'from-[#8B5CF6] to-[#6D28D9]' },
    { label: 'Credits Earned', value: (referralInfo?.total_credits_earned || 0).toFixed(1), icon: TrendingUp, color: 'from-[#10B981] to-[#059669]' },
    { label: 'Available Credits', value: (referralInfo?.reward_credits || 0).toFixed(1), icon: Award, color: 'from-[#3B82F6] to-[#2563EB]' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23]" data-testid="invite-friends-page">
      <Navbar />
      <div className="pt-24 pb-32 px-4 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(139,92,246,0.3)]">
            <Gift size={28} className="text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-syne" data-testid="invite-title">
            Invite Friends
          </h1>
          <p className="text-[#9CA3AF] mt-2 text-sm sm:text-base max-w-md mx-auto">
            Share EntrepreNatives with your network and earn Reward Credits for every successful referral
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-4 text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                    <stat.icon size={18} className="text-white" />
                  </div>
                  <p className="text-xl font-bold text-white">{stat.value}</p>
                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Promo Code Card */}
            <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-[#8B5CF6]/20 rounded-2xl p-6 shadow-[0_0_40px_rgba(139,92,246,0.1)]">
              <p className="text-xs text-[#9CA3AF] mb-2 font-medium uppercase tracking-wider">Your Promo Code</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-[#0E0E23]/60 border border-white/10 rounded-xl px-5 py-3 font-mono text-2xl text-white font-bold tracking-[0.2em] text-center" data-testid="promo-code-display">
                  {referralInfo?.promo_code || '--------'}
                </div>
                <button
                  onClick={copyCode}
                  className="p-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] transition-all"
                  data-testid="copy-code-btn"
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
            </div>

            {/* Referral Link */}
            <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
              <p className="text-xs text-[#9CA3AF] mb-2 font-medium uppercase tracking-wider">Your Referral Link</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-[#0E0E23]/60 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-[#A78BFA] truncate font-mono" data-testid="referral-link-display">
                  {referralLink}
                </div>
                <button
                  onClick={copyLink}
                  className="p-2.5 rounded-xl border border-white/10 text-[#9CA3AF] hover:text-white hover:border-[#8B5CF6]/30 transition-all flex-shrink-0"
                  data-testid="copy-link-btn"
                >
                  {copiedLink ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={shareNative}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-[#1E1E3A]/80 border border-white/5 hover:border-[#8B5CF6]/30 transition-all"
                data-testid="share-native-btn"
              >
                <Share2 size={20} className="text-[#A78BFA]" />
                <span className="text-xs text-[#9CA3AF]">Share</span>
              </button>
              <button
                onClick={shareWhatsApp}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-[#1E1E3A]/80 border border-white/5 hover:border-[#25D366]/30 transition-all"
                data-testid="share-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#25D366] fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span className="text-xs text-[#9CA3AF]">WhatsApp</span>
              </button>
              <button
                onClick={shareTwitter}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-[#1E1E3A]/80 border border-white/5 hover:border-[#1DA1F2]/30 transition-all"
                data-testid="share-twitter-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#1DA1F2] fill-current"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                <span className="text-xs text-[#9CA3AF]">Twitter</span>
              </button>
            </div>

            {/* How it works */}
            <div className="bg-[#1E1E3A]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-4">How It Works</h3>
              <div className="space-y-4">
                {[
                  { num: '1', title: 'Share your code or link', desc: 'Send it to friends, post on social media, or text it directly' },
                  { num: '2', title: 'Friends sign up & hire vendors', desc: 'When they use your code to browse and book services' },
                  { num: '3', title: 'Earn 3% Reward Credits', desc: 'Get credits for every successful transaction they make' },
                ].map((step) => (
                  <div key={step.num} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center flex-shrink-0 text-xs font-bold text-white">
                      {step.num}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{step.title}</p>
                      <p className="text-xs text-[#9CA3AF]">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Supporter Dashboard CTA */}
            {user?.roles?.includes('supporter') && (
              <Link
                to="/dashboard/supporter"
                className="block text-center py-3 rounded-xl border border-[#8B5CF6]/20 text-[#A78BFA] text-sm hover:bg-[#8B5CF6]/10 transition-all"
                data-testid="go-to-supporter-dashboard"
              >
                View full Supporter Dashboard & redeem credits
              </Link>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
